import React, { useLayoutEffect, useRef, useState } from 'react';

/**
 * CardMarquee — a row of cards that auto-scrolls AND can be scrubbed by hand.
 *
 *   - Auto-scroll is a pure CSS transform animation on a duplicated track
 *     (compositor-driven, reliable, never stuck on JS state).
 *   - The container is a native horizontal scroller, so a 2-finger trackpad
 *     swipe (desktop) or a finger drag (mobile) scrolls the cards both ways.
 *   - The animation pauses while you hover or scrub, and resumes shortly after
 *     you stop — toggled by writing animation-play-state directly to the DOM
 *     (no React state, so a re-render can never leave it stuck paused).
 *   - The CSS animation only moves `transform`, never `scrollLeft`, so the
 *     scroll handler reliably means "the user scrubbed" (no feedback loop).
 *   - `touch-action: pan-x pan-y`: horizontal drags scrub the row, vertical
 *     drags scroll the page — so you can always scroll up/down with a finger
 *     on a card, and taps still register as clicks.
 *   - `reverse` flips the direction (runs scroll one way, clubs the other).
 */
const RESUME_MS = 280;

const CardMarquee = ({
  items,
  renderItem,
  reverse = false,
  speedPxPerSec = 26,
  cardWidthClass = 'w-[300px] sm:w-[340px]',
}) => {
  const wrapRef = useRef(null);
  const trackRef = useRef(null);
  const [durationS, setDurationS] = useState(60);
  const st = useRef({ hovering: false, scrolling: false, timer: 0, centered: false });

  const count = items ? items.length : 0;
  // Repeat into several copies so the manual-scroll offset (up to one copy) and
  // the animation offset (up to one copy) always have content on both sides.
  const REPEATS = count ? Math.max(3, Math.ceil(30 / count)) : 0;
  const tiles = [];
  for (let r = 0; r < REPEATS; r += 1) tiles.push(...items);

  // Derive the animation duration from the measured width so the px/sec speed
  // is consistent. Center the scroll once so there's room to scrub either way.
  useLayoutEffect(() => {
    const track = trackRef.current;
    const wrap = wrapRef.current;
    if (!track || !wrap) return undefined;
    const measure = () => {
      const set = track.scrollWidth / REPEATS; // width of one copy
      if (set <= 0) return;
      setDurationS(Math.max(15, set / Math.max(4, speedPxPerSec)));
      if (!st.current.centered) {
        wrap.scrollLeft = set; // start one copy in
        st.current.centered = true;
      }
    };
    measure();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(track);
    }
    track.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', measure, { once: true });
    });
    return () => { if (ro) ro.disconnect(); };
  }, [count, speedPxPerSec, REPEATS]);

  if (!count) return null;

  const setPaused = (p) => {
    const el = trackRef.current;
    if (el) el.style.animationPlayState = p ? 'paused' : 'running';
  };
  const refresh = () => setPaused(st.current.hovering || st.current.scrolling);
  const scheduleResume = () => {
    clearTimeout(st.current.timer);
    st.current.timer = setTimeout(() => { st.current.scrolling = false; refresh(); }, RESUME_MS);
  };
  // Keep the manual scroll position within one copy of the seam so scrubbing
  // loops infinitely. The CSS animation never touches scrollLeft, so this only
  // ever runs for a real user scroll.
  const wrapScroll = () => {
    const wrap = wrapRef.current;
    const track = trackRef.current;
    if (!wrap || !track) return;
    const set = track.scrollWidth / REPEATS;
    if (set <= 0) return;
    if (wrap.scrollLeft >= set * 2) wrap.scrollLeft -= set;
    else if (wrap.scrollLeft <= 0) wrap.scrollLeft += set;
  };

  const onScroll = () => { wrapScroll(); st.current.scrolling = true; refresh(); scheduleResume(); };
  const onMouseEnter = () => { st.current.hovering = true; refresh(); };
  const onMouseLeave = () => { st.current.hovering = false; refresh(); scheduleResume(); };

  const pct = (100 / REPEATS).toFixed(4);

  return (
    <div
      ref={wrapRef}
      className="rc-cm-scroll relative overflow-x-auto overflow-y-hidden"
      style={{ touchAction: 'pan-x pan-y' }}
      onScroll={onScroll}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <style>{`
        @keyframes rc-cm-fwd { from { transform: translate3d(0,0,0); } to { transform: translate3d(-${pct}%,0,0); } }
        @keyframes rc-cm-rev { from { transform: translate3d(-${pct}%,0,0); } to { transform: translate3d(0,0,0); } }
        .rc-cm-scroll::-webkit-scrollbar { display: none; }
        .rc-cm-scroll { scrollbar-width: none; -ms-overflow-style: none; }
      `}</style>

      <div
        ref={trackRef}
        className="rc-cm-track flex w-max gap-4 sm:gap-6 py-4"
        style={{
          animation: `${reverse ? 'rc-cm-rev' : 'rc-cm-fwd'} ${durationS}s linear infinite`,
          willChange: 'transform',
        }}
      >
        {tiles.map((item, i) => (
          <div key={i} className={`flex-shrink-0 ${cardWidthClass}`} aria-hidden={i >= count}>
            {renderItem(item, i)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CardMarquee;
